import React from 'react';

export default function AbstractSection() {
  return (
    <section className="py-20 bg-white border-t border-ink-100">
      <div className="max-w-5xl mx-auto px-6">
        <p className="section-eyebrow mb-3">01 &middot; Overview</p>
        <h2 className="section-title">Abstract</h2>

        <div className="mt-10 prose-paper space-y-5 max-w-3xl mx-auto">
          <p>
            Backdoor attacks pose severe security threats to large language
            models (LLMs), where a model behaves normally under benign inputs
            but produces malicious outputs when a hidden trigger appears.
            Existing backdoor removal methods typically assume prior knowledge
            of triggers, access to a clean reference model, or rely on
            aggressive finetuning configurations, and are often limited to
            classification tasks &mdash; such assumptions fall apart in
            real-world generative LLM settings.
          </p>
          <p>
            We propose a new framework for purifying generative LLMs without
            any prior trigger knowledge or clean references. Systematic sanity
            checks show that backdoor associations are{' '}
            <strong>redundantly encoded across MLP layers</strong>, while
            attention modules primarily{' '}
            <strong>amplify trigger signals</strong> without establishing the
            behavior. We therefore shift the focus from isolating specific
            triggers to <strong>cutting off the trigger&ndash;behavior
            associations</strong>, designing an immunization-inspired
            elimination approach: construct multiple synthetic backdoored
            variants of the suspicious model with different malicious
            trigger&ndash;behavior pairs, contrast them with their clean
            counterparts, and let the recurring modifications across variants
            reveal a shared{' '}
            <strong>"backdoor signature"</strong> &mdash; analogous to
            antigens in a virus.
          </p>
          <p>
            Guided by this signature, we neutralize the highly suspicious
            components and apply lightweight finetuning to restore fluency,
            producing purified models that withstand diverse backdoor attacks
            and threat models while preserving generative capability.
          </p>
        </div>

        {/* TL;DR callout */}
        <div className="mt-10 max-w-3xl mx-auto">
          <div className="relative rounded-2xl bg-grad-callout p-[1px] shadow-card">
            <div className="rounded-2xl bg-white p-6 md:p-7">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-md bg-brand-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-white">
                  TL;DR
                </span>
              </div>
              <p className="mt-3 text-base md:text-lg font-serif italic text-ink-800 leading-relaxed">
                Treat the suspicious model like a virus carrier: synthesize
                variants with our own triggers, diff each variant against its
                clean counterpart, and the shared MLP updates expose the
                backdoor &mdash; zeroing those components plus a small clean
                fine-tune drives <strong className="not-italic">ASR to near&nbsp;0%</strong>{' '}
                while keeping utility intact.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
